import React from 'react';
import { useNavigate } from 'react-router-dom';

import styles from 'prototypes/securedCard/app.module.scss';
import AppChrome from 'prototypes/securedCard/components/AppChrome';
import PaycheckControl from 'prototypes/securedCard/components/PaycheckControl';
import { formatMoney } from 'prototypes/securedCard/formatMoney';
import { PrototypePath } from 'prototypes/securedCard/paths';
import { SPENDABLE_PERCENT } from 'prototypes/securedCard/prototypeAssumptions';
import StepFooter from 'prototypes/securedCard/components/StepFooter';
import { cadencePhrase } from 'prototypes/securedCard/storyMath';
import { useStory } from 'prototypes/securedCard/StoryContext';

const onePaycheck = (cents: number) => Math.floor((cents * SPENDABLE_PERCENT) / 100);

const PaycheckStep = () => {
  const navigate = useNavigate();
  const { state, setPerPaycheckCents, setCadence, confirmPaycheck } = useStory();
  const available = onePaycheck(state.perPaycheckCents);
  const held = state.perPaycheckCents - available;

  return (
    <AppChrome
      step={PrototypePath.Paycheck}
      title="Put part of each paycheck here"
      subtitle={`Same kind of payroll deduction as a Plannery loan. You choose it once. ${cadencePhrase(
        state.cadence,
      )}, it lands in savings on its own.`}
    >
      <div className={styles.stack}>
        <PaycheckControl
          cents={state.perPaycheckCents}
          cadence={state.cadence}
          idPrefix="paycheck"
          onCents={setPerPaycheckCents}
          onCadence={setCadence}
        />
        <div className={styles.figureList}>
          <div className={`${styles.figure} ${styles.figurePrimary}`}>
            <div>
              <p className={styles.figureLabel}>Ready on the card</p>
              <p className={styles.figureHint}>{SPENDABLE_PERCENT}% of this paycheck</p>
            </div>
            <p className={styles.figureValue}>{formatMoney(available)}</p>
          </div>
          <div className={styles.figure}>
            <div>
              <p className={styles.figureLabel}>Stays saved</p>
              <p className={styles.figureHint}>Still yours. It keeps the account growing.</p>
            </div>
            <p className={styles.figureValue}>{formatMoney(held)}</p>
          </div>
        </div>
        <p className={styles.notice}>
          You are not opening a separate savings habit. The deduction is the saving. The next paycheck adds to it, and
          the card limit rises with it.
        </p>
      </div>
      <StepFooter
        backTo={PrototypePath.Welcome}
        nextLabel="Use this deduction"
        onNext={() => {
          confirmPaycheck();
          navigate(PrototypePath.Growth);
        }}
      />
    </AppChrome>
  );
};

export default PaycheckStep;
